'use client';

import { Box, HStack } from '@terra/ui-styled-system';
import { ArrowDown01Icon, ArrowRight01Icon } from '@terra/ui-icons';
import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Collapsible } from '../../components/collapsible';
import { HeadingItem } from '../../components/typography';
import { Button } from '../../components/button';

export type DrawerCollapsibleVariant = 'default' | 'box';
export type DrawerCollapsibleProps = {
  title: string;
  children: React.ReactNode;
  onViewAllClick?: () => void;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  variant?: DrawerCollapsibleVariant;
  action?: React.ReactNode;
  hideHeaderDivider?: boolean;
  dense?: boolean;
};
export function DrawerCollapsible({
  title,
  children,
  onViewAllClick,
  defaultOpen = true,
  onOpenChange,
  variant = 'default',
  action,
  hideHeaderDivider = false,
  dense = false,
}: DrawerCollapsibleProps) {
  const t = useTranslations('designSystem.customDrawer');
  const [isExpanded, setIsExpanded] = useState(defaultOpen);

  useEffect(() => {
    setIsExpanded(defaultOpen);
  }, [defaultOpen]);
  const header = (
    <HStack
      gap={2}
      alignItems="center"
      w="full"
      justifyContent="space-between"
      py={2}
      px={variant === 'box' ? 4 : dense ? 0 : 2}
    >
      <HStack gap={2} alignItems="center" flex={1} minW={0}>
        <Collapsible.Trigger asChild>
          <Box
            as="button"
            flex={1}
            minW={0}
            gap={2}
            cursor="pointer"
            bg="transparent"
            border="none"
            textAlign="left"
            display="flex"
            alignItems="center"
            aria-label={isExpanded ? t('collapse') : t('expand')}
          >
            <HeadingItem as="span" color="fg.default" truncate>
              {title}
            </HeadingItem>
            <Box as="span" flexShrink={0} display="inline-flex" aria-hidden>
              {isExpanded ? (
                <ArrowDown01Icon size={16} />
              ) : (
                <ArrowRight01Icon size={16} />
              )}
            </Box>
          </Box>
        </Collapsible.Trigger>
      </HStack>
      {action}
      {onViewAllClick && !action && (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onViewAllClick();
          }}
        >
          {t('viewAll')}
        </Button>
      )}
    </HStack>
  );
  const content = (
    <Collapsible.Content>
      <Box
        pt={variant === 'box' ? (dense ? 3 : 6) : dense ? 1 : 2}
        pb={variant === 'box' ? (dense ? 3 : 6) : undefined}
        px={variant === 'box' ? (dense ? 3 : 4) : dense ? 0 : 2}
      >
        {children}
      </Box>
    </Collapsible.Content>
  );
  const root = (
    <Collapsible.Root
      open={isExpanded}
      onOpenChange={({ open }) => {
        setIsExpanded(open);
        onOpenChange?.(open);
      }}
    >
      {variant === 'box' ? (
        <Box
          w="full"
          bg="bg.neutral.default"
          borderWidth="1px"
          borderColor="border.subtle"
          borderRadius="lg"
          overflow="hidden"
        >
          {header}
          {isExpanded && !hideHeaderDivider ? (
            <Box w="full" borderTopWidth="1px" borderColor="border.subtle" />
          ) : null}
          {content}
        </Box>
      ) : (
        <>
          {header}
          {content}
        </>
      )}
    </Collapsible.Root>
  );

  return root;
}
